import { useState, useEffect, useRef, useCallback } from 'react';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import styles from './ContextualToolbar.module.css';

interface ContextualToolbarProps {
	onFormat: (syntax: string) => void;
	onUndo: () => void;
	onRedo: () => void;
	onClearFormat: () => void;
	onIndent: () => void;
	onOutdent: () => void;
	canUndo: boolean;
	canRedo: boolean;
}

interface Position {
	x: number;
	y: number;
}

const TOOLBAR_CONFIG = {
	width: 450,
	height: 50,
	offsetY: 10,
	offsetX: 20,
	hideDelay: 2500,
};

export const ContextualToolbar = ({
	onFormat,
	onUndo,
	onRedo,
	onClearFormat,
	onIndent,
	onOutdent,
	canUndo,
	canRedo,
}: ContextualToolbarProps) => {
	const [isVisible, setIsVisible] = useState(false);
	const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
	const toolbarRef = useRef<HTMLDivElement>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
	const isInteractingRef = useRef(false);

	const calculatePosition = useCallback((rect: DOMRect): Position => {
		const scrollX = window.scrollX;
		const scrollY = window.scrollY;

		let x = rect.left + rect.width / 2 - TOOLBAR_CONFIG.width / 2 + scrollX;
		let y = rect.top - TOOLBAR_CONFIG.height - TOOLBAR_CONFIG.offsetY + scrollY;

		if (x < TOOLBAR_CONFIG.offsetX) {
			x = TOOLBAR_CONFIG.offsetX;
		} else if (x + TOOLBAR_CONFIG.width > window.innerWidth - TOOLBAR_CONFIG.offsetX) {
			x = window.innerWidth - TOOLBAR_CONFIG.width - TOOLBAR_CONFIG.offsetX;
		}

		if (y < scrollY + TOOLBAR_CONFIG.offsetX) {
			y = rect.bottom + TOOLBAR_CONFIG.offsetY + scrollY;
		}

		return { x, y };
	}, []);

	const clearHideTimeout = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = undefined;
		}
	}, []);

	const startHideTimeout = useCallback(() => {
		clearHideTimeout();
		if (!isInteractingRef.current) {
			timeoutRef.current = setTimeout(() => {
				setIsVisible(false);
			}, TOOLBAR_CONFIG.hideDelay);
		}
	}, [clearHideTimeout]);

	const handleSelectionChange = useCallback(() => {
		if (isInteractingRef.current) return;

		const selection = globalThis.getSelection();

		if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
			setIsVisible(false);
			return;
		}

		const range = selection.getRangeAt(0);
		const rect = range.getBoundingClientRect();
		const selectedText = selection.toString().trim();

		const container = range.commonAncestorContainer;
		const element = container.nodeType === 1 ? (container as Element) : container.parentElement;
		const isEditable = element?.closest('[contenteditable="true"]');

		if (!isEditable || !selectedText) {
			setIsVisible(false);
			return;
		}

		if (rect.width > 0 && rect.height > 0) {
			const newPosition = calculatePosition(rect);
			setPosition(newPosition);
			setIsVisible(true);
			startHideTimeout();
		}
	}, [calculatePosition, startHideTimeout]);

	const handleToolbarMouseEnter = useCallback(() => {
		isInteractingRef.current = true;
		clearHideTimeout();
	}, [clearHideTimeout]);

	const handleToolbarMouseLeave = useCallback(() => {
		isInteractingRef.current = false;
		startHideTimeout();
	}, [startHideTimeout]);

	const handleAction = useCallback(
		(action: () => void) => {
			action();
			startHideTimeout();
		},
		[startHideTimeout]
	);

	useEffect(() => {
		document.addEventListener('selectionchange', handleSelectionChange);
		document.addEventListener('mouseup', handleSelectionChange);
		document.addEventListener('keyup', handleSelectionChange);

		return () => {
			document.removeEventListener('selectionchange', handleSelectionChange);
			document.removeEventListener('mouseup', handleSelectionChange);
			document.removeEventListener('keyup', handleSelectionChange);
			clearHideTimeout();
		};
	}, [handleSelectionChange, clearHideTimeout]);

	if (!isVisible) return null;

	return (
		<div
			ref={toolbarRef}
			className={styles.contextualToolbar}
			style={{
				position: 'absolute',
				top: `${position.y}px`,
				left: `${position.x}px`,
				zIndex: 9999,
			}}
			onMouseEnter={handleToolbarMouseEnter}
			onMouseLeave={handleToolbarMouseLeave}
			onMouseDown={(e) => e.preventDefault()}
			role="toolbar"
			aria-label="Barra de ferramentas de formatação"
		>
			<div className={styles.toolbarContent}>
				<button
					type="button"
					className={styles.toolbarButton}
					onClick={() => handleAction(onUndo)}
					disabled={!canUndo}
					title="Desfazer (Ctrl+Z)"
				>
					<UndoIcon />
				</button>
				<button
					type="button"
					className={styles.toolbarButton}
					onClick={() => handleAction(onRedo)}
					disabled={!canRedo}
					title="Refazer (Ctrl+Y)"
				>
					<RedoIcon />
				</button>
				<div className={styles.toolbarDivider} />
				<button
					type="button"
					className={styles.toolbarButton}
					onClick={() => handleAction(() => onFormat('bold'))}
					title="Negrito (Ctrl+B)"
				>
					<FormatBoldIcon />
				</button>
				<button
					type="button"
					className={styles.toolbarButton}
					onClick={() => handleAction(() => onFormat('italic'))}
					title="Itálico (Ctrl+I)"
				>
					<FormatItalicIcon />
				</button>
				<div className={styles.toolbarDivider} />
				<button
					type="button"
					className={styles.toolbarButton}
					onClick={() => handleAction(() => onFormat('h1'))}
					title="Título"
				>
					H1
				</button>
				<button
					type="button"
					className={styles.toolbarButton}
					onClick={() => handleAction(() => onFormat('h2'))}
					title="Subtítulo"
				>
					H2
				</button>
				<div className={styles.toolbarDivider} />
				<button
					type="button"
					className={styles.toolbarButton}
					onClick={() => handleAction(() => onFormat('ul'))}
					title="Lista"
				>
					<FormatListBulletedIcon />
				</button>
				<button
					type="button"
					className={styles.toolbarButton}
					onClick={() => handleAction(() => onFormat('ol'))}
					title="Lista numerada"
				>
					<FormatListNumberedIcon />
				</button>
				<div className={styles.toolbarDivider} />
				<button
					type="button"
					className={styles.toolbarButton}
					onClick={() => handleAction(onIndent)}
					title="Aumentar recuo (Tab)"
				>
					<FormatIndentIncreaseIcon />
				</button>
				<button
					type="button"
					className={styles.toolbarButton}
					onClick={() => handleAction(onOutdent)}
					title="Diminuir recuo (Shift+Tab)"
				>
					<FormatIndentDecreaseIcon />
				</button>
				<div className={styles.toolbarDivider} />
				<button
					type="button"
					className={styles.toolbarButton}
					onClick={() => handleAction(onClearFormat)}
					title="Limpar formatação"
				>
					<FormatClearIcon />
				</button>
			</div>
		</div>
	);
};
